import React from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Premium dark-mode 4-step progress bar for the restaurant onboarding wizard.
 *
 * Displayed at the top of every onboarding screen so owners always know
 * (a) where they are, (b) how much is left, (c) that progress is preserved.
 *
 * iter116 — Stepper grown from 3 → 4 steps. A new "Create Account" slot was
 * added at the front so the signup form moves INTO the wizard, giving
 * owners a tangible result (an account they can come back to) before being
 * asked to pick a plan or connect Stripe. Order: 1 Create Account → 2 Plan
 * → 3 Stripe → 4 Go live.
 *
 * Numbers are ALWAYS rendered inside the step circle — even on completed
 * steps. We use color (emerald = done, gold = current, neutral = future) to
 * communicate completion instead of replacing the digit with a checkmark, so
 * the customer always sees "1, 2, 3, 4" no matter where they are in the wizard.
 *
 * Click-to-navigate — every step is a real button that lands on the
 * corresponding page so the owner can jump back to review or edit.
 */
const STEPS = [
  { key: 'account_created', short: 'Account',  full: 'Create your account',    route: '/onboarding/account' },
  { key: 'choose_plan',     short: 'Plan',     full: 'Choose plan',            route: '/onboarding/plan' },
  { key: 'stripe_connect',  short: 'Stripe',   full: 'Connect Stripe',         route: '/onboarding/stripe-connect' },
  { key: 'subscribe',       short: 'Trial',    full: '45-day free trial',      route: '/onboarding/trial' },
  { key: 'setup',           short: 'Go live',  full: 'Setup & publish',        route: '/onboarding/setup' },
];

/**
 * Returns the index of the currently-active step given the backend cursor.
 * Legacy keys (`payout_trust`) collapse forward to the closest visible step.
 * iter141 — `subscribe` is now its own visible step (Trial, idx 3); `setup`
 * moves to idx 4. Restaurants whose persisted cursor is on `subscribe`
 * now surface at the correct step instead of being lumped in with Go live.
 */
const indexFromBackend = (backendStep) => {
  switch (backendStep) {
    case 'account_created': return 0;
    case 'choose_plan':     return 1;
    case 'stripe_connect':  return 2;
    case 'subscribe':       return 3;
    case 'setup':           return 4;
    case 'done':            return 5;
    case 'payout_trust':    return 1;  // legacy — surface as "Plan" pending
    default:                return 0;
  }
};

export const OnboardingProgress = ({ currentStep = 'choose_plan', className = '' }) => {
  const navigate = useNavigate();
  const activeIdx = indexFromBackend(currentStep);
  const pct = Math.min(100, (activeIdx / (STEPS.length - 1)) * 100);

  // iter123 — Free navigation across the stepper. Forward steps render in
  // a "locked preview" mode (the page itself shows a banner + a click-
  // shield that prevents form interaction), so the owner CAN see what's
  // coming next without being able to fill it in out of order. This is
  // the iter115 pattern (Step 4 "Setup locked") generalised to Step 3 as
  // well. Removed the iter121 toast block here — that was too aggressive
  // (the owner could not even peek at upcoming steps).
  // iter143 — Clicking a stepper button is an EXPLICIT navigation
  // intent ("take me to this step so I can edit it"). We append
  // `?edit=1` so the destination page can distinguish deliberate
  // stepper clicks from stale redirects / bookmark hits, and let the
  // owner reach the wizard even after their restaurant has already
  // gone live. Without this, an already-published restaurant tapping
  // "5 Go live" would be sent straight to /dashboard by the iter136
  // "already live? go to dashboard" guard on OnboardingSetupPage.
  const goTo = (step) => {
    if (!step?.route) return;
    const url = step.route.includes('?')
      ? `${step.route}&edit=1`
      : `${step.route}?edit=1`;
    navigate(url);
  };

  return (
    <div
      className={`w-full ${className}`}
      data-testid="onboarding-progress"
      data-current-step={currentStep}
    >
      {/* Step labels — desktop */}
      <ol className="hidden md:flex items-start justify-between gap-2 mb-4">
        {STEPS.map((s, i) => {
          const completed = i < activeIdx;
          const active = i === activeIdx;
          return (
            <li
              key={s.key}
              className="flex-1 min-w-0 flex justify-center"
              aria-current={active ? 'step' : undefined}
            >
              <button
                type="button"
                onClick={() => goTo(s)}
                data-testid={`onboarding-step-${s.key}`}
                aria-label={`Step ${i + 1} — ${s.full}${completed ? ' (completed)' : active ? ' (current)' : ''}. Click to review.`}
                className="group flex flex-col items-center text-center w-full px-1 py-1 rounded-lg hover:bg-white/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400/60 transition-all cursor-pointer"
              >
                <div
                  className={`w-9 h-9 rounded-full flex items-center justify-center mb-2 border transition-all ${
                    completed
                      ? 'bg-emerald-500/15 border-emerald-500/50 text-emerald-300 group-hover:border-emerald-400 group-hover:bg-emerald-500/25'
                      : active
                        ? 'bg-gradient-to-b from-yellow-300 to-yellow-500 border-yellow-300 text-black shadow-[0_8px_20px_-8px_rgba(250,204,21,0.6)] group-hover:shadow-[0_10px_24px_-8px_rgba(250,204,21,0.8)]'
                        : 'bg-white/[0.03] border-white/15 text-white/40 group-hover:border-yellow-400/40 group-hover:text-white/70 group-hover:bg-white/[0.06]'
                  }`}
                >
                  {/* iter113 — Always show the step number, never replace it
                      with a checkmark. The color of the circle communicates
                      completion state on its own. */}
                  <span className="text-[12.5px] font-bold tabular-nums">{i + 1}</span>
                </div>
                <span
                  className={`text-[11px] leading-tight font-semibold tracking-wide truncate w-full transition-colors ${
                    active
                      ? 'text-white'
                      : completed
                        ? 'text-emerald-300/85 group-hover:text-emerald-200'
                        : 'text-white/40 group-hover:text-white/70'
                  }`}
                >
                  {s.short}
                </span>
              </button>
            </li>
          );
        })}
      </ol>

      {/* Compact bar — mobile */}
      <div className="md:hidden mb-4">
        <div className="flex items-center justify-between mb-2 text-[11px] text-white/55 font-medium tracking-wide">
          <span data-testid="onboarding-progress-label-mobile">
            {activeIdx >= STEPS.length
              ? <span className="text-emerald-300 font-bold">All steps complete</span>
              : <>Step {activeIdx + 1} of {STEPS.length}
                  <span className="mx-1.5 text-white/30">·</span>
                  <span className="text-white">{STEPS[activeIdx]?.full}</span>
                </>}
          </span>
          <span className="text-yellow-400 font-bold">{Math.round(pct)}%</span>
        </div>

        {/* Mobile clickable chips — small horizontal scroll row so owners can
            still jump between steps on mobile without losing the wizard. The
            number on the chip mirrors the desktop circle so the customer
            always sees "1 Account · 2 Plan · 3 Stripe · 4 Go live". */}
        <div
          className="-mx-1 flex items-center gap-1.5 overflow-x-auto no-scrollbar pb-1"
          data-testid="onboarding-progress-mobile-chips"
        >
          {STEPS.map((s, i) => {
            const completed = i < activeIdx;
            const active = i === activeIdx;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => goTo(s)}
                data-testid={`onboarding-step-mobile-${s.key}`}
                aria-label={`Jump to step ${i + 1} — ${s.full}`}
                className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10.5px] font-bold tracking-wide transition-all ${
                  completed
                    ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                    : active
                      ? 'border-yellow-400/60 bg-yellow-500/15 text-yellow-300'
                      : 'border-white/10 bg-white/[0.03] text-white/50'
                }`}
              >
                <span className="font-mono">{i + 1}</span>
                <span>·</span>
                <span>{s.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Connector bar (gold fill = progress) — desktop + mobile */}
      <div className="relative h-1 rounded-full bg-white/[0.05] overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-yellow-400 to-amber-500 transition-[width] duration-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

export default OnboardingProgress;
